import * as Sentry from "@sentry/react";
import axios from "axios";
import { useEffect, useRef } from "react";

import { address } from "@api/index";

export type AddressSuggestion = {
  street_line: string;
  secondary?: string;
  city: string;
  state: string;
  zipcode: string;
  full_address: string;
  entries?: number;
};

interface AddressSearchResponse {
  suggestions: AddressSuggestion[];
}

/**
 * Cancellation-safe search against Runway's own address-search proxy (never
 * Smarty directly) -- see docs/domains/address-search-proxy/index.md. A
 * newer call always aborts the previous in-flight request so a stale
 * response can't overwrite a fresher one; an aborted request resolves to an
 * empty array rather than rejecting.
 */
const useAddressSuggestions = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const getSuggestionResults = async (query: string): Promise<AddressSuggestion[]> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let suggestions: AddressSuggestion[] = [];
    try {
      const { data } = await address.addressGet(`/search?search=${encodeURIComponent(query)}`, {
        signal: controller.signal,
      });
      const { suggestions: results } = data as AddressSearchResponse;
      suggestions = results
        .filter((result) => !result.street_line.includes("PO Box"))
        .map((result) => {
          const { entries, secondary, ...cleanObject } = result;
          return { ...cleanObject, full_address: Object.values(cleanObject).join(" ") };
        });
    } catch (err) {
      if (axios.isCancel(err)) {
        // Superseded by a newer keystroke's request -- not a real failure.
        return suggestions;
      }
      Sentry.captureException(err);
    }
    return suggestions;
  };

  return { getSuggestionResults };
};

export default useAddressSuggestions;
